import { PackageFields } from '@openx/types/package.ts';
import { TargetingFields } from '@openx/types/targeting/targeting.ts';
import { noop } from '@openx/utils/noop.ts';
import { CursorClickIcon } from '@phosphor-icons/react/CursorClick';
import { ScreencastIcon } from '@phosphor-icons/react/Screencast';
import { SubtitlesIcon } from '@phosphor-icons/react/Subtitles';
import { VideoIcon } from '@phosphor-icons/react/Video';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type Section, TargetingElementKeys, type TargetingElementKeysType } from '../../context/types.ts';
import { convertApiToSet } from '../../utils/convertApiToSet.ts';
import { useFormatOperations } from '../useFormatOperations.ts';

export const useFormatInitialState = (): Section => {
  const { t } = useTranslation();
  const { getValues } = useFormContext() || { getValues: noop };
  const { onBannerOrNativeClick, onCTVClick, onVideoClick } = useFormatOperations();

  return useMemo(() => {
    const formValue = getValues(`${PackageFields.TARGETING}.${TargetingFields.INVENTORY_FORMAT}.ad_placement.val`);
    const initValues = convertApiToSet(formValue);
    const isSelected = (key: TargetingElementKeysType): boolean => (initValues.size ? initValues.has(key) : false);
    const isDisabled = (key: TargetingElementKeysType): boolean => (initValues.size ? !initValues.has(key) : false);

    return {
      label: t('Format'),
      isLoading: true,
      elements: [
        {
          mainOption: {
            key: TargetingElementKeys.FORMAT_BANNER,
            label: t('Banner'),
            icon: <SubtitlesIcon size={24} />,
            isSelected: isSelected(TargetingElementKeys.FORMAT_BANNER),
            isDisabled: isDisabled(TargetingElementKeys.FORMAT_BANNER),
            onClick: onBannerOrNativeClick,
          },
        },
        {
          mainOption: {
            key: TargetingElementKeys.FORMAT_VIDEO,
            label: t('Video'),
            icon: <VideoIcon size={24} />,
            isSelected: isSelected(TargetingElementKeys.FORMAT_VIDEO),
            isDisabled: isDisabled(TargetingElementKeys.FORMAT_VIDEO),
            onClick: onVideoClick,
          },
        },
        {
          mainOption: {
            key: TargetingElementKeys.FORMAT_CTV,
            label: t('CTV'),
            icon: <ScreencastIcon size={24} />,
            isSelected: isSelected(TargetingElementKeys.FORMAT_CTV),
            isDisabled: isDisabled(TargetingElementKeys.FORMAT_CTV),
            onClick: onCTVClick,
          },
        },
        {
          mainOption: {
            key: TargetingElementKeys.FORMAT_NATIVE,
            label: t('Native'),
            icon: <CursorClickIcon size={24} />,
            isSelected: isSelected(TargetingElementKeys.FORMAT_NATIVE),
            isDisabled: isDisabled(TargetingElementKeys.FORMAT_NATIVE),
            onClick: onBannerOrNativeClick,
          },
        },
      ],
    };
  }, [t, getValues, onCTVClick, onBannerOrNativeClick, onVideoClick]);
};
